import axios from 'axios'
import $store from '@/store/index'
import AgoraRTC from 'agora-rtc-sdk-ng'
AgoraRTC.disableLogUpload()
// AgoraRTC.enableLogUpload()
AgoraRTC.setLogLevel(2)
let channelList = {}
class RTCClient {
  constructor(callBack) {
    this._client = null
    this._joined = false
    this._callBack = callBack
    this._params = {}
    this.remoteStream = {}
  }

  handleEvents () {
    // Occurs when the peer user leaves the channel; for example, the peer user calls Client.leave.
    this._client.on('user-left', (user) => {
      const { uid } = user
      if (uid != this._params.uid) {
        this._callBack({
          action: 'user-left',
          payload: {
            id: uid
          }
        })
      }
    })
    this._client.on('connection-state-change', (curState, revState, reason) => {
      const { uid, channel } = this._params
      this._callBack({
        action: 'connection-state-change',
        payload: {
          id: uid,
          channel,
          curState,
          revState,
          reason
        }
      })
    })
    // Occurs when the remote stream is added.
    this._client.on('user-published', async (remoteUser, mediaType) => {
      this.subscribe(remoteUser, mediaType)
    })
    // Occurs when the remote stream is removed; for example, a peer user calls Client.unpublish.
    this._client.on('user-unpublished', (remoteUser, mediaType) => {
      const { uid } = remoteUser
      if (!(this.remoteStream[uid] && this.remoteStream[uid].stream)) return false
      if (mediaType === 'audio') {
        if (!this.remoteStream[uid].stream.getAudioTracks().length) return
        this._callBack({
          action: 'user-unpublished',
          payload: {
            id: uid,
            mediaType
          }
        })
        this.remoteStream[uid].stream.removeTrack(this.remoteStream && this.remoteStream[uid] && this.remoteStream[uid].stream.getAudioTracks()[0])
      } else if (mediaType === 'video') {
        if (!this.remoteStream[uid].stream.getVideoTracks().length) return
        this._callBack({
          action: 'user-unpublished',
          payload: {
            id: uid,
            mediaType
          }
        })
        this.remoteStream[uid].stream.removeTrack(this.remoteStream[uid].stream.getVideoTracks()[0])
      } else {
        this.remoteStream[uid].stream = null
        this._callBack({
          action: 'user-left',
          payload: {
            id: uid
          }
        })
      }
    })
  }

  async join (data) {
    const channelKey = data.channel
    if (Object.hasOwn(channelList, channelKey)) {
      this._joined = false
      try {
        // leave 后再 destroy，释放 Agora 内部解码器和 worker 资源，避免内存泄漏 [Claude Code]
        await channelList[channelKey].leave()
        channelList[channelKey].removeAllListeners()
        delete channelList[channelKey]
      } catch (error) {
        delete channelList[channelKey]
      }
    }
    if (this._joined) return
    /**
     * A class defining the properties of the config parameter in the createClient method.
     * Note:
     *    Ensure that you do not leave mode and codec as empty.
     *    Ensure that you set these properties before calling Client.join.
     *  You could find more detail here. https://docs.agora.io/en/Video/API%20Reference/web/interfaces/agorartc.clientconfig.html
     **/
    // Safari 不支持 vp8，需使用 h264 以避免 videoTrack.configuration 崩溃 [Claude Code]
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
    this._client = AgoraRTC.createClient({
      mode: data.mode || 'live',
      codec: data.codec || (isSafari ? 'h264' : 'vp8')
    })
    this._client.setClientRole('audience', { level: 1 })
    this._params = data
    if (this._params.useProxy) {
      this._client.startProxyServer(2)
    }
    // handle AgoraRTC client event
    this.handleEvents()
    // Join channel
    try {
      const { appID, token, channel, uid } = data
      const id = await this._client.join(appID, channel, token, uid)
      this._params.uid = id
      this._params.channel = channel
      this._joined = true
      channelList[channelKey] = this._client
      return id
    } catch (e) {
      this._joined = false
    }
    // finally {
    //   delete channelList[channelKey]
    // }
  }

  async subscribe (remoteUser, mediaType) {
    const { uid } = remoteUser
    // 重新订阅前，先清理旧 track，防止解码失败重试时 MediaStream 累积泄漏 [Claude Code]
    const existing = this.remoteStream[uid]
    if (existing && existing.stream) {
      const tracksToRemove = mediaType === 'audio'
        ? existing.stream.getAudioTracks()
        : mediaType === 'video'
          ? existing.stream.getVideoTracks()
          : existing.stream.getTracks()
      tracksToRemove.forEach(track => {
        track.stop()
        existing.stream.removeTrack(track)
      })
    }
    this.remoteStream[uid] = {
      id: uid,
      channel: this._params.channel,
      stream: existing && existing.stream || new MediaStream(),
      isAudio: existing && existing.isAudio ? true : remoteUser.hasAudio,
      isVideo: existing && existing.isVideo ? true : remoteUser.hasVideo
    }
    try {
      await this._client.subscribe(remoteUser, mediaType)
      // 添加空值保护，防止 Safari 上 track 为 null 导致崩溃 [Claude Code]
      if ((mediaType === 'audio' || mediaType === 'all') && remoteUser._audioTrack?._mediaStreamTrack) {
        this.remoteStream[uid].stream.addTrack(remoteUser._audioTrack._mediaStreamTrack)
        // 监听 audio track 底层替换事件，保持 MediaStream 同步 [Claude Code]
        if (remoteUser.audioTrack) this.trackUpdate(remoteUser.audioTrack, uid)
      }
      if ((mediaType === 'video' || mediaType === 'all') && remoteUser._videoTrack?._mediaStreamTrack) {
        this.remoteStream[uid].stream.addTrack(remoteUser._videoTrack._mediaStreamTrack)
        // 监听 video track 底层替换事件，保持 MediaStream 同步 [Claude Code]
        if (remoteUser.videoTrack) this.trackUpdate(remoteUser.videoTrack, uid)
      }
    } catch (e) {
      console.log(e, '订阅失败')
    }
    this._callBack({
      action: 'user-published',
      payload: {
        id: uid,
        proInfo: false,
        remoteStream: this.remoteStream[uid],
        channel: this._params.channel,
        mediaType,
        remoteUser
      }
    })
  }

  trackUpdate (track, uid) {
    // 监听 Agora track 底层替换事件，保持 MediaStream 与最新 track 同步 [Claude Code]
    const mediaType = track.kind
    const handlerKey = mediaType === 'audio' ? '_trackUpdateHandlerAudio' : '_trackUpdateHandlerVideo'
    const trackKey = mediaType === 'audio' ? '_trackUpdateTrackAudio' : '_trackUpdateTrackVideo'
    // 先解绑旧 handler，防止重复注册 [Claude Code]
    if (this.remoteStream[uid]?.[handlerKey]) {
      this.remoteStream[uid][trackKey]?.off('track-updated', this.remoteStream[uid][handlerKey])
    }
    const handler = (newTrack) => {
      const type = newTrack.kind
      if (type === 'video') {
        const oldTrack = this.remoteStream[uid]?.stream?.getVideoTracks()[0]
        if (oldTrack) {
          this.remoteStream[uid].stream.removeTrack(oldTrack)
          this.remoteStream[uid].stream.addTrack(newTrack)
        }
      } else if (type === 'audio') {
        const oldTrack = this.remoteStream[uid]?.stream?.getAudioTracks()[0]
        if (oldTrack) {
          this.remoteStream[uid].stream.removeTrack(oldTrack)
          this.remoteStream[uid].stream.addTrack(newTrack)
        }
      }
      // 复用 user-published 链路，让 preview.js 无感知地更新 stream [Claude Code]
      this._callBack({
        action: 'user-published',
        payload: {
          id: uid,
          proInfo: false,
          remoteStream: this.remoteStream[uid],
          channel: this._params.channel,
          mediaType: type
        }
      })
    }
    this.remoteStream[uid][handlerKey] = handler
    this.remoteStream[uid][trackKey] = track
    track.on('track-updated', handler)
  }

  async leave () {
    if (!this._client || !this._joined) return
    try {
      // 离开前停止所有远端 track，并清理 trackUpdate 监听，防止内存泄漏 [Claude Code]
      Object.values(this.remoteStream).forEach((remoteStreamItem) => {
        const { stream } = remoteStreamItem
        if (stream) stream.getTracks().forEach(t => t.stop())
        if (remoteStreamItem._trackUpdateTrackAudio && remoteStreamItem._trackUpdateHandlerAudio) {
          remoteStreamItem._trackUpdateTrackAudio.off('track-updated', remoteStreamItem._trackUpdateHandlerAudio)
        }
        if (remoteStreamItem._trackUpdateTrackVideo && remoteStreamItem._trackUpdateHandlerVideo) {
          remoteStreamItem._trackUpdateTrackVideo.off('track-updated', remoteStreamItem._trackUpdateHandlerVideo)
        }
      })
      this.remoteStream = {}
      await this._client.leave()
      this._joined = false
    } catch (e) {
      console.log(e)
    }
  }
}

export default class RTIL {
  constructor(options, callBack) {
    this.code = options.code
    this.callBack = callBack || function () { }
    this.uid = ''
    this.RTCClient = null
    this.memberId = this.generateUniqueId()
    this.rtcCallBack = this.rtcCallBack.bind(this)
    this.joinParty()
  }

  joinParty () {
    const email = $store.state.userInfo.email
    const memberId = this.memberId
    const params = {
      memberId: email + '_mediahub_' + memberId,
      partyCode: this.code,
      role: 1,
      type: 0
    }
    const url = `${window.location.protocol}//${$store.state.initConfig.gatewayUrl}/rtil/party/joinParty`
    axios.post(url, params, {
      withCredentials: true
    }).then(res => {
      if (res.data.errorCode == '0x0') {
        this.initRtcClient(res.data.result, this.code)
      } else {
        this.callBack({ action: 'error', result: res.data.errorInfo })
      }
    }).catch((err) => { // 40260/40258
      this.callBack({ action: 'error', result: err })
    })
  }

  // 存储用到这个id的code
  generateUniqueId (reset) {
    // 尝试从 sessionStorage 中获取标识
    let memberId = sessionStorage.getItem('memberId')
    // 如果不存在，则生成一个新的标识
    if (!memberId || reset || this.getCodeStorage(this.code, memberId)) {
      memberId = this.generateComplexRandomId()
      // memberId = '06f9cb9f-8cd7-4b18-y4bc-866eed372302'
      // 将新生成的标识存储到 sessionStorage 中
      sessionStorage.setItem('memberId', memberId)
    }
    this.setCodeStorage(this.code, memberId)
    return memberId
  }

  setCodeStorage (code, memberId) {
    const CodeStorage = window.CodeStorage || {}
    if (CodeStorage[memberId]) {
      CodeStorage[memberId].push(code)
    } else {
      CodeStorage[memberId] = [code]
    }
    window.CodeStorage = CodeStorage
  }

  getCodeStorage (code, memberId) {
    const CodeStorage = window.CodeStorage || {}
    if (CodeStorage[memberId]) {
      return CodeStorage[memberId].includes(code)
    } else {
      return false
    }
  }

  removeCodeStorage (code, memberId) {
    const CodeStorage = window.CodeStorage || {}
    if (CodeStorage[memberId]) {
      const index = CodeStorage[memberId].indexOf(code)
      if (index > -1) {
        CodeStorage[memberId].splice(index, 1)
      }
      // return CodeStorage[memberId].includes(code)
    } else {
      return false
    }
  }

  generateComplexRandomId () {
    const s4 = () =>
      Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .substring(1)
    return (
      s4() +
      s4() +
      '-' +
      s4() +
      '-4' +
      s4().substring(0, 3) +
      '-y' +
      s4().substring(1, 4) +
      '-' +
      s4() +
      s4() +
      s4()
    )
  }

  initRtcClient (result, channel) {
    const data = {
      appID: result.appId,
      channel: channel.toString(),
      token: result.tokenCode,
      codec: 'vp8',
      audioSource: true,
      uid: result.rtilCode
    }
    this.uid = result.rtilCode
    this.RTCClient = new RTCClient(this.rtcCallBack)
    this.RTCClient.join(data)
  }

  rtcCallBack ({ action, payload }) {
    switch (action) {
      case 'user-unpublished':
        this.userUnpublished(payload)
        break
      case 'user-published':
        this.userPublished(payload)
        break
      case 'user-left':
        this.removeView(payload)
        break
      case 'connection-state-change':
        if (payload.reason === 'UID_BANNED') {
          this.againGetMemberId()
        }
        break
    }
  }

  userPublished (payload) {
    try {
      this.callBack({ action: 'stream', result: payload })
    } catch (error) {
      console.log(error)
    }
  }

  userUnpublished (payload) {
    try {
      this.callBack({ action: 'user-unpublished', result: payload })
    } catch (error) {
      console.log(error)
    }
  }

  removeView () {
    // console.log('removeView')
  }

  // 再次获得成员id
  againGetMemberId () {
    // 尝试从 sessionStorage 中获取标识
    const memberId = sessionStorage.getItem('memberId')
    this.memberId = this.generateUniqueId(memberId === this.memberId)
    this.joinParty()
  }

  destroy () {
    this.removeCodeStorage(this.code, this.memberId)
    // this.RTCClient._client.leave()
    this.RTCClient?.leave()
  }
}
